import useHashConnect from "hooks/useHashConnect";
import useAccount from "hooks/useUserAccount";
import { nativeTokenFormatter } from "utils/CurrencyHelper";
import { ConnectType } from "context/HashConnectProvider";
import React, { useCallback, useEffect } from "react";
import { ButtonWrapper, ModalWrapper } from "ui";
import WalletDisconnect from "./WalletDisconnect";
import WalletConnect from "./WalletConnect";
import { Box, Text, useColorModeValue } from "@chakra-ui/react";
import hederaDark from "assets/hederaDark.svg";
import hederaLight from "assets/hederaLight.svg";
import Image from "next/image";

// firebase analytics
import { firebaseEvents } from "utils/firebase";
import { FIREBASE_EVENT_PROPERTIES } from "constants/constants";

export const WalletStatus = {
  WALLET_CONNECTED: "WALLET_CONNECTED",
  INITIALIZING: "INITIALIZING",
  WALLET_NOT_CONNECTED: "WALLET_NOT_CONNECTED",
};

const ConnectWalletDropdown = () => {
  const {
    connect,
    disconnect,
    selectedAccount,
    status,
    installedExtensions,
    isBladeExtensionInstalled,
    walletData,
    walletModal,
    showWalletModal,
  } = useHashConnect();
  const { hbar } = useAccount();
  const isWalletConnected: boolean = status === WalletStatus.WALLET_CONNECTED;
  const isWalletInitializing: boolean = status === WalletStatus.INITIALIZING;
  const isWalletDisconnected: boolean =
    status === WalletStatus.WALLET_NOT_CONNECTED;

  const hederaIcon = useColorModeValue(hederaLight, hederaDark);
  const sendLog = (name: string) => {
    if (walletData.pairedAccounts) {
      let firebaseRequest: any = {
        [FIREBASE_EVENT_PROPERTIES.EVENT_NAME]: name,
        [FIREBASE_EVENT_PROPERTIES.WALLET_NAME]:
          walletData.pairedWalletData?.name,
        [FIREBASE_EVENT_PROPERTIES.WALLET_ADDRESS]:
          walletData.pairedAccounts[0],
      };
      firebaseEvents(firebaseRequest);
    }
  };

  const connectWalletModal = (
    <WalletConnect
      installedExtensions={installedExtensions}
      isWalletInitializing={isWalletInitializing}
      isWalletDisconnected={isWalletDisconnected}
      isBladeExtensionInstalled={isBladeExtensionInstalled}
      installWallet={(type: ConnectType) => {
        showWalletModal(false);
        connect(type);
      }}
    />
  );

  const disconnectWalletModal = (
    <WalletDisconnect
      walletBalance={hbar}
      disconnectWallet={() => {
        sendLog("Disconnect_wallet");
        // trackEvent(DISCONNECT_WALLET_CLICK, { address: selectedAccount });
        showWalletModal(false);
        disconnect();
      }}
      walletAddress={selectedAccount}
      walletData={walletData}
    />
  );

  const renderWalletButton = useCallback(() => {
    if (isWalletDisconnected || isWalletInitializing) {
      return (
        <ButtonWrapper
          onClick={() => showWalletModal(true)}
          size="lg"
          bg="brand.primary"
          textColor="brand.textLight"
        >
          Connect wallet
        </ButtonWrapper>
      );
    }
    return (
      <ButtonWrapper
        onClick={() => showWalletModal(true)}
        size="lg"
        _hover={{
          bg: "nill",
        }}
      >
        <Box mr="1rem" mt="0.25rem">
          <Image src={hederaIcon} width={20} height={20} />
        </Box>
        <Text as="span" fontSize="20px">
          {nativeTokenFormatter(hbar)}
        </Text>
        <Text as="span" fontSize="12px" px={2} pt={0.5}>
          HBAR
        </Text>
        <Text
          bg="brand.background"
          as="span"
          fontSize="12px"
          p="4px 16px"
          ml="8px"
          borderRadius="8px"
          color="brand.textColor"
          display={{ base: "none", xl: "flex" }}
        >
          {selectedAccount}
        </Text>
      </ButtonWrapper>
    );
  }, [isWalletDisconnected, isWalletInitializing, hbar, selectedAccount]);

  useEffect(() => {
    if (walletModal) {
      sendLog("wallet_modal");
    }
  }, [walletModal]);

  return (
    <div style={{ position: "relative" }}>
      <div>{renderWalletButton()}</div>

      <ModalWrapper
        onClose={() => showWalletModal(false)}
        modal={walletModal}
        body={isWalletConnected ? disconnectWalletModal : connectWalletModal}
        header={
          isWalletConnected ? (
            <Box textColor="brand.color" mt={0.5}>
              Wallet
            </Box>
          ) : (
            <Box color="brand.color" mt={0.5}>
              Select wallet
            </Box>
          )
        }
      />
    </div>
  );
};

export default ConnectWalletDropdown;
